import { h } from "preact";
import type { InputHTMLAttributes, SelectHTMLAttributes, TextareaHTMLAttributes } from "preact";
import { cx } from "./lib/cx.ts";

type Own = { class?: string };
// Preact 11 types an element's attributes as a union of ARIA role variants; a
// plain Omit would merge the variants into props the element no longer accepts.
type OmitEach<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;
type InputAttrs = Own & OmitEach<InputHTMLAttributes<HTMLInputElement>, "class" | "size">;
type SelectAttrs = Own & OmitEach<SelectHTMLAttributes<HTMLSelectElement>, "class" | "size">;
type TextareaAttrs = Own & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "class">;

/**
 * A single-line text field. Rule: filled and borderless; focus shows an inset
 * 2px ring instead of a border, so it stays visible inside clipped toolbars.
 * Always give it an `aria-label` or a visible label.
 */
export function TextInput({ class: className, ...rest }: InputAttrs) {
  return <input type="text" {...rest} class={cx("ui-input", className)} />;
}

/** A native select with a chevron. Rule: native on purpose (keyboard, mobile and screen readers for free); same filled, borderless look and focus ring as TextInput.
 *  Where the browser styles selects (`appearance: base-select`), the chosen value renders in a `selectedcontent`
 *  element, so a long value stays on one line and ends in an ellipsis. Other browsers ignore the button. */
export function Select({ class: className, children, ...rest }: SelectAttrs) {
  return (
    <span class="ui-select-wrap">
      <select {...rest} class={cx("ui-select", className)}>
        <button type="button" class="ui-select__value" tabIndex={-1}>{h("selectedcontent", null)}</button>
        {children}
      </select>
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M4 6l4 4 4-4" />
      </svg>
    </span>
  );
}

/** A multi-line field. Rule: same filled, borderless look as TextInput; grows to at most 30% of the viewport, then scrolls. */
export function Textarea({ class: className, ...rest }: TextareaAttrs) {
  return <textarea {...rest} class={cx("ui-textarea", className)} />;
}

/** An amount of money: a TextInput with the currency mark inside its left edge and a decimal keypad on phones. Rule: the mark is part of the field's look, not its value. */
export function MoneyInput({ currency = "$", class: className, ...rest }: InputAttrs & { currency?: string }) {
  return (
    <span class={cx("ui-money", className)}>
      <span class="ui-money__mark" aria-hidden="true">{currency}</span>
      <input type="text" inputMode="decimal" {...rest} class="ui-input ui-money__input" />
    </span>
  );
}
